// src/components/Investment/InvestmentPlanCard.jsx - FIXED CURRENCY CONVERSION
import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import WithdrawalModal from './WithdrawalModal';
import './InvestmentPlanCard.css';

const InvestmentPlanCard = ({ investment, onWithdrawalSuccess }) => {
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const { formatCurrency, currency } = useCurrency();

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  // Convert NGN to user's display currency
  const convertFromNGN = (ngnAmount) => {
    if (!ngnAmount) return 0;
    
    const amount = parseFloat(ngnAmount);
    
    if (currency === 'NGN' || !exchangeRate) {
      return amount;
    }
    
    // Convert NGN to USD
    const rate = parseFloat(exchangeRate.ngn_to_usd);
    return amount * rate;
  };

  // Check if investment is truly matured
  const isInvestmentMatured = () => {
    if (investment.status === 'MATURED') return true;
    if (investment.is_matured === true) return true;
    
    if (investment.lock_end_date) {
      const endDate = new Date(investment.lock_end_date);
      const now = new Date();
      return now >= endDate;
    }
    
    return false;
  };

  // FIXED: Format amount with proper conversion
  const formatAmount = (field) => {
    const displayField = `${field}_display`;
    
    // Check if backend provided pre-formatted display
    if (investment[displayField] && investment[displayField].formatted) {
      return investment[displayField].formatted;
    }
    
    // Backend stores in NGN, convert if needed
    const amountInNGN = investment[field] || 0;
    const convertedAmount = convertFromNGN(amountInNGN);
    return formatCurrency(convertedAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = () => {
    if (isInvestmentMatured()) {
      return <span className="status-badge matured">Matured</span>;
    }
    
    switch (investment.status) {
      case 'ACTIVE':
        return <span className="status-badge active">Active</span>;
      case 'WITHDRAWN':
        return <span className="status-badge withdrawn">Withdrawn</span>;
      case 'PARTIALLY_WITHDRAWN':
        return <span className="status-badge partial">Partially Withdrawn</span>;
      default:
        return <span className="status-badge">Unknown</span>;
    }
  };

  const getDaysRemaining = () => {
    if (investment.status === 'WITHDRAWN') {
      return null;
    }

    if (isInvestmentMatured()) {
      return 'Matured';
    }

    const endDate = new Date(investment.lock_end_date);
    const currentDate = new Date();
    const diffTime = endDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Matured';
    if (diffDays === 0) return 'Ends today';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };

  const canWithdraw = () => {
    if (investment.status === 'WITHDRAWN') return false;
    
    if (isInvestmentMatured()) return true;
    
    if (investment.withdrawal_preference !== 'NO_WITHDRAWAL') {
      return investment.can_process_withdrawal || true;
    }
    
    return false;
  };

  const getWithdrawalButtonText = () => {
    if (investment.status === 'WITHDRAWN') return 'Fully Withdrawn';
    
    if (isInvestmentMatured()) return 'Withdraw All';
    
    if (investment.withdrawal_preference === 'PROFIT_PERIODIC') {
      return 'Withdraw Profit';
    } else if (investment.withdrawal_preference === 'FIXED_PERIODIC') {
      return 'Periodic Withdrawal';
    } else {
      return 'Withdraw';
    }
  };

  return (
    <>
      <div className="investment-plan-card">
        {/* Header */}
        <div className="card-header">
          <div className="stock-info">
            <h3 className="stock-symbol">{investment.stock?.symbol}</h3>
            <p className="stock-name">{investment.stock?.name}</p>
            {currency && (
              <span className="currency-badge">{currency}</span>
            )}
          </div>
          <div className="status-section">
            {getStatusBadge()}
            {getDaysRemaining() && (
              <span className="days-remaining">{getDaysRemaining()}</span>
            )}
          </div>
        </div>

        {/* Investment Details */}
        <div className="investment-details">
          <div className="detail-row">
            <span className="label">Invested Amount:</span>
            <span className="value">{formatAmount('total_invested')}</span>
          </div>
          
          <div className="detail-row">
            <span className="label">Current Value:</span>
            <span className="value">{formatAmount('current_value')}</span>
          </div>
          
          <div className="detail-row">
            <span className="label">Profit/Loss:</span>
            <span className={`value ${parseFloat(investment.profit_loss) >= 0 ? 'positive' : 'negative'}`}>
              {formatAmount('profit_loss')} ({investment.profit_loss_percentage}%)
            </span>
          </div>
          
          <div className="detail-row">
            <span className="label">Shares:</span>
            <span className="value">{parseFloat(investment.quantity).toFixed(6)}</span>
          </div>

          {investment.lock_end_date && (
            <div className="detail-row">
              <span className="label">{isInvestmentMatured() ? 'Matured On:' : 'Matures On:'}</span>
              <span className="value">{formatDate(investment.lock_end_date)}</span>
            </div>
          )}

          {/* Withdrawal Plan */}
          <div className="withdrawal-plan">
            <span className="label">Withdrawal Plan:</span>
            <span className="value">
              {investment.withdrawal_preference === 'PROFIT_PERIODIC' && 
                `Profit (${investment.profit_withdrawal_percentage}%) every 4 weeks`
              }
              {investment.withdrawal_preference === 'FIXED_PERIODIC' && 
                `Fixed (${formatAmount('fixed_withdrawal_amount')}) every 4 weeks`
              }
              {investment.withdrawal_preference === 'NO_WITHDRAWAL' && 
                'At maturity only'
              }
            </span>
          </div>

          {/* Show withdrawn amounts if any - FIXED */}
          {investment.total_amount_withdrawn && parseFloat(investment.total_amount_withdrawn) > 0 && (
            <div className="detail-row highlight">
              <span className="label">Total Withdrawn:</span>
              <span className="value success">{formatAmount('total_amount_withdrawn')}</span>
            </div>
          )}
        </div>

        {/* Exchange Rate Info (optional) */}
        {exchangeRate && currency === 'USD' && (
          <div className="exchange-rate-info">
            <small>
              Rate: 1 USD = ₦{parseFloat(exchangeRate.usd_to_ngn).toFixed(2)} 
              <span className="rate-source"> (CoinGecko)</span>
            </small>
          </div>
        )}

        {/* Action Buttons */}
        <div className="card-actions">
          <button
            className={`withdraw-button ${!canWithdraw() ? 'disabled' : ''}`}
            onClick={() => setShowWithdrawalModal(true)}
            disabled={!canWithdraw()}
          >
            {getWithdrawalButtonText()}
          </button>
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <WithdrawalModal
          investment={investment}
          onClose={() => setShowWithdrawalModal(false)}
          onSuccess={() => {
            setShowWithdrawalModal(false);
            onWithdrawalSuccess();
          }}
        />
      )}
    </>
  );
};

export default InvestmentPlanCard;